import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Package, AlertTriangle, Plus, ArrowUpCircle, ArrowDownCircle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

const CATEGORY_COLORS = {
  protein: 'bg-blue-100 text-blue-700',
  bcaa: 'bg-green-100 text-green-700',
  creatine: 'bg-purple-100 text-purple-700',
  vitamin: 'bg-orange-100 text-orange-700',
  pre_workout: 'bg-red-100 text-red-700',
  accessory: 'bg-gray-100 text-gray-700',
  other: 'bg-slate-100 text-slate-700',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function InventoryTab({ isAdmin }) {
  const [stockLevels, setStockLevels] = useState([]);
  const [alerts, setAlerts] = useState({ low_stock: [], never_stocked: [] });
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 50;

  // Filters
  const [txProductFilter, setTxProductFilter] = useState('all');
  const [txTypeFilter, setTxTypeFilter] = useState('all');
  const [txDateFrom, setTxDateFrom] = useState('');
  const [txDateTo, setTxDateTo] = useState('');

  // Stock-in dialog
  const [showStockIn, setShowStockIn] = useState(false);
  const [stockInForm, setStockInForm] = useState({ product_id: '', quantity: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchStockLevels = useCallback(async () => {
    try {
      const [levelsRes, alertsRes] = await Promise.all([
        axiosInstance.get('/supplements/inventory/stock-levels'),
        axiosInstance.get('/supplements/inventory/alerts', { params: { threshold: 10 } }),
      ]);
      setStockLevels(levelsRes.data?.stock_levels || []);
      setAlerts(alertsRes.data || { low_stock: [], never_stocked: [] });
    } catch {
      toast.error('Failed to load stock levels');
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/supplements/products');
      setProducts(res.data?.products || []);
    } catch {
      // non-critical
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const params = { page, limit };
      if (txProductFilter && txProductFilter !== 'all') params.product_id = txProductFilter;
      if (txTypeFilter && txTypeFilter !== 'all') params.transaction_type = txTypeFilter;
      if (txDateFrom) params.date_from = txDateFrom;
      if (txDateTo) params.date_to = txDateTo;
      const res = await axiosInstance.get('/supplements/inventory/transactions', { params });
      const data = res.data || {};
      setTransactions(data.transactions || data.items || data || []);
      setTotalPages(data.total_pages || Math.ceil((data.total || 0) / limit) || 1);
    } catch {
      toast.error('Failed to load transactions');
    }
  }, [page, txProductFilter, txTypeFilter, txDateFrom, txDateTo]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchStockLevels(), fetchProducts()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchStockLevels, fetchProducts]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleStockIn = async () => {
    if (!stockInForm.product_id) { toast.error('Please select a product'); return; }
    const qty = Number(stockInForm.quantity);
    if (!qty || qty <= 0) { toast.error('Quantity must be greater than 0'); return; }

    setSubmitting(true);
    try {
      await axiosInstance.post('/supplements/inventory/stock-in', {
        product_id: stockInForm.product_id,
        quantity: qty,
        notes: stockInForm.notes.trim() || null,
      });
      toast.success('Stock recorded successfully');
      setShowStockIn(false);
      setStockInForm({ product_id: '', quantity: '', notes: '' });
      fetchStockLevels();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record stock');
    } finally {
      setSubmitting(false);
    }
  };

  // Build lookup sets for low stock and never stocked product IDs
  const lowStockIds = new Set((alerts.low_stock || []).map(a => a.product_id));
  const neverStockedIds = new Set((alerts.never_stocked || []).map(a => a.product_id || a.id));

  const lowStockCount = lowStockIds.size;
  const neverStockedCount = neverStockedIds.size;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Inventory Management</h2>
          <p className="text-sm text-muted-foreground">{stockLevels.length} products tracked</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowStockIn(true)} data-testid="add-stock-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Stock
          </Button>
        )}
      </div>

      {/* Summary Card */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-slate-500" />
              <span className="text-slate-600">Total Products:</span>
              <span className="font-semibold text-slate-800">{stockLevels.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-slate-600">Low Stock:</span>
              <span className={`font-semibold ${lowStockCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {lowStockCount}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-orange-500" />
              <span className="text-slate-600">Never Stocked:</span>
              <span className={`font-semibold ${neverStockedCount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {neverStockedCount}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section A: Stock Levels Dashboard */}
      <h3 className="text-md font-semibold text-slate-700 mb-3">Stock Levels</h3>
      {stockLevels.length === 0 ? (
        <Card className="mb-6">
          <CardContent className="p-8 text-center">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-muted-foreground">No inventory data yet. Add stock to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {stockLevels.map((item) => {
            const isLow = lowStockIds.has(item.product_id);
            const isNever = neverStockedIds.has(item.product_id);
            return (
              <Card
                key={item.product_id}
                className={`${isLow ? 'border-red-300 bg-red-50/50' : isNever ? 'border-orange-300 bg-orange-50/50' : ''}`}
                data-testid={`stock-card-${item.product_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-slate-800 text-sm">{item.product_name}</div>
                    <Badge variant="outline" className={CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other}>
                      {item.category}
                    </Badge>
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mb-1">
                    {item.current ?? 0}
                    <span className="text-sm font-normal text-slate-500 ml-1">{item.unit}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
                    <span className="flex items-center gap-1">
                      <ArrowUpCircle className="w-3 h-3 text-green-500" />
                      In: {item.stock_in ?? 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <ArrowDownCircle className="w-3 h-3 text-red-500" />
                      Out: {item.stock_out ?? 0}
                    </span>
                  </div>
                  {isLow && (
                    <Badge className="bg-red-100 text-red-700 border-red-300">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      LOW STOCK
                    </Badge>
                  )}
                  {isNever && (
                    <Badge className="bg-orange-100 text-orange-700 border-orange-300">
                      <Package className="w-3 h-3 mr-1" />
                      NEVER STOCKED
                    </Badge>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Section C: Transaction History */}
      <h3 className="text-md font-semibold text-slate-700 mb-3">Transaction History</h3>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select value={txProductFilter} onValueChange={(val) => { setTxProductFilter(val); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="tx-product-filter">
            <SelectValue placeholder="All Products" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            {products.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={txTypeFilter} onValueChange={(val) => { setTxTypeFilter(val); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="tx-type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="stock_in">Stock In</SelectItem>
            <SelectItem value="stock_out">Stock Out</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={txDateFrom}
            onChange={(e) => { setTxDateFrom(e.target.value); setPage(1); }}
            className="w-40"
            placeholder="From"
          />
          <span className="text-slate-400">to</span>
          <Input
            type="date"
            value={txDateTo}
            onChange={(e) => { setTxDateTo(e.target.value); setPage(1); }}
            className="w-40"
            placeholder="To"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="text-left px-4 py-3 text-sm font-medium">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Product</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Type</th>
                  <th className="text-right px-4 py-3 text-sm font-medium">Quantity</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Reference</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Recorded By</th>
                  <th className="text-left px-4 py-3 text-sm font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(Array.isArray(transactions) ? transactions : []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  (Array.isArray(transactions) ? transactions : []).map((tx, idx) => (
                    <tr key={tx.id || idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-600">{formatDate(tx.recorded_at || tx.created_at || tx.date)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-800">{tx.product_name || '\u2014'}</td>
                      <td className="px-4 py-3">
                        {tx.transaction_type === 'stock_in' ? (
                          <Badge className="bg-green-100 text-green-700 border-green-300">
                            <ArrowUpCircle className="w-3 h-3 mr-1" />
                            Stock In
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 border-red-300">
                            <ArrowDownCircle className="w-3 h-3 mr-1" />
                            Stock Out
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-800">{tx.quantity}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {tx.reference_type === 'assignment' && tx.reference_id
                          ? <span className="text-blue-600 underline cursor-pointer">Assignment</span>
                          : tx.reference || '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{tx.recorded_by_name || tx.recorded_by || '\u2014'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500 max-w-[200px] truncate">{tx.notes || '\u2014'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Stock-In Dialog */}
      <Dialog open={showStockIn} onOpenChange={setShowStockIn}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Stock In</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product *</Label>
              <Select
                value={stockInForm.product_id}
                onValueChange={(val) => setStockInForm(prev => ({ ...prev, product_id: val }))}
              >
                <SelectTrigger data-testid="stockin-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantity *</Label>
              <Input
                type="number"
                min="1"
                value={stockInForm.quantity}
                onChange={(e) => setStockInForm(prev => ({ ...prev, quantity: e.target.value }))}
                placeholder="Enter quantity"
                data-testid="stockin-quantity-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={stockInForm.notes}
                onChange={(e) => setStockInForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes (e.g. supplier, batch number)"
                rows={3}
                data-testid="stockin-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStockIn(false)}>Cancel</Button>
            <Button onClick={handleStockIn} disabled={submitting} data-testid="submit-stockin-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {submitting ? 'Recording...' : 'Record Stock In'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
